import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { formatCurrency } from '../../utils';
import type { LeaderboardEntry } from '../../types';

interface LeaderboardProps {
  entries: LeaderboardEntry[];
  currentParticipantId?: string;
  onUserClick?: (participantId: string) => void;
}

/**
 * Leaderboard Component
 * Displays ranked list of participants by winnings balance
 */
export function Leaderboard({ entries, currentParticipantId, onUserClick }: LeaderboardProps) {
  if (entries.length === 0) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <p className="text-secondary-text">No participants yet</p>
          <p className="text-muted-text text-sm mt-2">
            Join the event to see the leaderboard
          </p>
        </div>
      </Card>
    );
  }

  const getRankDisplay = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'text-gold';
    if (rank === 2) return 'text-secondary-text';
    if (rank === 3) return 'text-warning-yellow';
    return 'text-primary-text';
  };

  return (
    <div className="space-y-3">
      {entries.map((entry) => {
        const isCurrentUser = entry.participantId === currentParticipantId;
        
        return (
          <Card
            key={entry.participantId}
            padding="md"
            className={`${isCurrentUser ? 'border-2 border-primary-blue bg-primary-blue/5' : ''} ${onUserClick ? 'cursor-pointer hover:border-primary-blue' : ''}`}
            onClick={onUserClick ? () => onUserClick(entry.participantId) : undefined}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1">
                {/* Rank */}
                <div className={`text-2xl font-bold ${getRankColor(entry.rank)}`}>
                  {getRankDisplay(entry.rank)}
                </div>

                {/* Participant Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className={`text-lg font-semibold text-primary-text ${onUserClick ? 'hover:text-primary-blue hover:underline' : ''}`}>
                      {entry.participantName}
                    </h3>
                    {isCurrentUser && (
                      <Badge variant="info" size="sm">
                        You
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-secondary-text">
                    <span>
                      {entry.wins}W - {entry.losses}L
                    </span>
                    <span>
                      {entry.winPercentage.toFixed(1)}% win rate
                    </span>
                    <span>
                      {entry.totalBets} bets
                    </span>
                  </div>
                  
                  {/* Extended Stats */}
                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                    <div className="bg-secondary-bg px-2 py-1 rounded">
                      <span className="text-muted-text">Total Bet:</span>{' '}
                      <span className="text-primary-text font-medium">
                        {formatCurrency(entry.totalAmountBet || 0)}
                      </span>
                    </div>
                    <div className="bg-secondary-bg px-2 py-1 rounded">
                      <span className="text-muted-text">Cash on Hand:</span>{' '}
                      <span className="text-primary-text font-medium">
                        {formatCurrency(entry.totalAvailable || 0)}
                      </span>
                    </div>
                    <div className="bg-secondary-bg px-2 py-1 rounded">
                      <span className="text-muted-text">Must Bet:</span>{' '}
                      <span className="text-primary-text font-medium">
                        {formatCurrency(entry.initialStakeRemaining || 0)}
                      </span>
                    </div>
                    <div className="bg-secondary-bg px-2 py-1 rounded">
                      <span className="text-muted-text">Lost:</span>{' '}
                      <span className="text-error-red font-medium">
                        {formatCurrency(entry.amountLost || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Winnings Balance */}
              <div className="text-right">
                <div className="text-sm text-secondary-text mb-1">Winnings</div>
                <div className={`text-2xl font-bold ${
                  entry.winningsBalance > 0 ? 'text-success-green' : 
                  entry.winningsBalance === 0 ? 'text-secondary-text' : 
                  'text-error-red'
                }`}>
                  {formatCurrency(entry.winningsBalance)}
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}


